import type {
  ConversationLastMessagePreviewDto,
  ConversationListItemDto,
  MessageDto,
  MessageTypeName,
} from "@multi-chat/shared";
import { messageHasVoiceAttachment } from "./attachment-kind.js";
import { interactiveFallbackLabel } from "./interactive-fallback-label.js";
import { locationCardTitle } from "./location-card.js";
import { unsupportedMessageLabel } from "./unsupported-label.js";

type Translate = (key: string) => string;

export function conversationTitle(item: ConversationListItemDto, t: Translate): string {
  const profile = item.contactPlatformProfile;
  if (profile.contactState === "ACTIVE" && profile.contact?.displayName) {
    return profile.contact.displayName;
  }
  if (profile.platformDisplayName?.trim()) {
    return profile.platformDisplayName.trim();
  }
  if (profile.normalizedPhoneNumber) {
    return profile.normalizedPhoneNumber;
  }
  return t("inbox.unsavedSender");
}

/** Phone/E.164 titles need LTR bidi isolation in RTL layouts. */
export function isPhoneLikeDisplay(value: string): boolean {
  return /^\+[\d][\d\s-]*$/.test(value.trim());
}

/** Compact initials for list avatars from an already-resolved display title. */
export function conversationInitials(title: string): string {
  const trimmed = title.trim();
  if (!trimmed) return "?";

  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    const first = parts[0]?.[0];
    const last = parts[parts.length - 1]?.[0];
    if (first && last) {
      return `${first}${last}`.toUpperCase();
    }
  }

  const single = parts[0] ?? trimmed;
  if (/^\+?\d[\d\s-]*$/.test(single)) {
    const digits = single.replace(/\D/g, "");
    return (digits.slice(-2) || "?").toUpperCase();
  }

  return single.slice(0, 2).toUpperCase();
}

export function previewLabel(
  preview: ConversationLastMessagePreviewDto | null,
  t: Translate,
): string {
  if (!preview) {
    return t("inbox.noMessagesYet");
  }
  if (preview.unsupportedKind) {
    return unsupportedMessageLabel(preview.unsupportedKind, t);
  }
  if (preview.messageType === "TEXT" || preview.messageType === "INTERACTIVE") {
    const text = preview.content?.trim();
    if (text && text.length > 0) {
      return text;
    }
    if (preview.messageType === "TEXT") {
      return t("inbox.emptyTextMessage");
    }
  }
  if (preview.messageType === "AUDIO") {
    return preview.providerVoice ? t("inbox.replyTo.voice") : t("inbox.replyTo.audio");
  }
  return messageTypeLabel(preview.messageType, t);
}

export function messageBodyLabel(message: MessageDto, t: Translate): string {
  if (message.unsupportedPresentation) {
    return unsupportedMessageLabel(message.unsupportedPresentation.kind, t);
  }
  if (message.locationPresentation) {
    return locationCardTitle(message.locationPresentation, t);
  }
  if (message.contactPresentation) {
    const formatted = message.contactPresentation.formattedName;
    if (formatted) {
      return formatted;
    }
    const parts = [
      message.contactPresentation.firstName,
      message.contactPresentation.lastName,
    ].filter((part): part is string => Boolean(part));
    if (parts.length > 0) {
      return parts.join(" ");
    }
    return t("inbox.messageType.CONTACT");
  }
  if (message.messageType === "TEXT" || message.messageType === "INTERACTIVE") {
    const text = message.content?.trim();
    if (text && text.length > 0) {
      return text;
    }
    if (message.messageType === "TEXT") {
      return t("inbox.emptyTextMessage");
    }
    if (
      message.interactivePresentation &&
      message.interactivePresentation.kind !== "button_reply" &&
      message.interactivePresentation.kind !== "list_reply"
    ) {
      return interactiveFallbackLabel(message.interactivePresentation, t);
    }
    return messageTypeLabel(message.messageType, t);
  }
  if (message.messageType === "AUDIO") {
    return messageHasVoiceAttachment(message.attachments)
      ? t("inbox.replyTo.voice")
      : t("inbox.replyTo.audio");
  }
  return messageTypeLabel(message.messageType, t);
}

export function messageTypeLabel(messageType: MessageTypeName, t: Translate): string {
  const key = `inbox.messageType.${messageType}`;
  const label = t(key);
  return label === key ? t("inbox.messageType.UNKNOWN") : label;
}

/** Newest-first API page → chronological ascending for thread display. */
export function chronologicalMessages(items: MessageDto[]): MessageDto[] {
  return items.toReversed();
}

/**
 * Merge a fresh first page into rows already loaded by pagination.
 * Drop previous rows inside the fresh window that the server omitted.
 * Keep the loaded tail only when the new page's last id is already in that
 * list, so the next cursor cannot skip a gap. An empty first page clears the
 * list. When the boundary is not loaded, keep the server page and its cursor.
 */
export function reconcileConversationFirstPage(input: {
  previous: readonly ConversationListItemDto[];
  firstPage: readonly ConversationListItemDto[];
  previousNextCursor: string | null;
  firstPageNextCursor: string | null;
}): { items: ConversationListItemDto[]; nextCursor: string | null } {
  if (input.firstPage.length === 0) {
    return { items: [], nextCursor: null };
  }
  const firstIds = new Set(input.firstPage.map((item) => item.conversationId));
  const boundaryId = input.firstPage[input.firstPage.length - 1]!.conversationId;
  const boundaryIndex = input.previous.findIndex((item) => item.conversationId === boundaryId);
  if (boundaryIndex < 0) {
    return {
      items: [...input.firstPage],
      nextCursor: input.firstPageNextCursor,
    };
  }
  const older = input.previous.filter((item, index) => {
    if (firstIds.has(item.conversationId)) {
      return false;
    }
    return index > boundaryIndex;
  });
  return {
    items: [...input.firstPage, ...older],
    nextCursor: older.length > 0 ? input.previousNextCursor : input.firstPageNextCursor,
  };
}

/** Server order for page 1, plus a proven older tail. */
export function mergeConversationFirstPage(
  previous: ConversationListItemDto[],
  firstPage: ConversationListItemDto[],
): ConversationListItemDto[] {
  return reconcileConversationFirstPage({
    previous,
    firstPage,
    previousNextCursor: null,
    firstPageNextCursor: null,
  }).items;
}

/** Conversation ids kept below the fresh first page after a merge. */
export function retainedTailConversationIds(
  merged: readonly ConversationListItemDto[],
  firstPage: readonly ConversationListItemDto[],
): number[] {
  const firstIds = new Set(firstPage.map((item) => item.conversationId));
  return merged
    .filter((item) => !firstIds.has(item.conversationId))
    .map((item) => item.conversationId);
}

export function appendConversations(
  previous: ConversationListItemDto[],
  page: ConversationListItemDto[],
): ConversationListItemDto[] {
  if (page.length === 0) return previous;
  const seen = new Set(previous.map((item) => item.conversationId));
  const appended = page.filter((item) => !seen.has(item.conversationId));
  return appended.length === 0 ? previous : [...previous, ...appended];
}

function messageSortAtMs(message: MessageDto): number {
  return Date.parse(message.platformSentAt ?? message.createdAt);
}

/** Stable ascending order matching server COALESCE(platform_sent_at, created_at), created_at, id. */
export function compareMessagesChronological(a: MessageDto, b: MessageDto): number {
  const sortDelta = messageSortAtMs(a) - messageSortAtMs(b);
  if (sortDelta !== 0) return sortDelta;
  const createdDelta = Date.parse(a.createdAt) - Date.parse(b.createdAt);
  if (createdDelta !== 0) return createdDelta;
  const aId = BigInt(a.messageId);
  const bId = BigInt(b.messageId);
  if (aId === bId) return 0;
  return aId < bId ? -1 : 1;
}

/** Merge refreshed newest page into loaded older history; UI stays chronological. */
export function mergeMessageFirstPage(
  previousChronological: MessageDto[],
  firstPageNewestFirst: MessageDto[],
): MessageDto[] {
  const firstChronological = chronologicalMessages(firstPageNewestFirst);
  const firstIds = new Set(firstChronological.map((item) => item.messageId));
  const older = previousChronological.filter((item) => !firstIds.has(item.messageId));
  return [...older, ...firstChronological].toSorted(compareMessagesChronological);
}

export function prependOlderMessages(
  previousChronological: MessageDto[],
  olderPageNewestFirst: MessageDto[],
): MessageDto[] {
  const olderChronological = chronologicalMessages(olderPageNewestFirst);
  if (olderChronological.length === 0) return previousChronological;
  const seen = new Set(previousChronological.map((item) => item.messageId));
  const uniqueOlder = olderChronological.filter((item) => !seen.has(item.messageId));
  if (uniqueOlder.length === 0) return previousChronological;
  return [...uniqueOlder, ...previousChronological];
}

export function isOutboundMessage(message: MessageDto): boolean {
  if (message.direction) {
    return message.direction === "OUTBOUND";
  }
  return message.organizationMemberId != null || message.origin != null;
}

/**
 * Newest inbound message id from a newest-first API page only.
 * Does not consider older paginated history.
 */
export function newestInboundMessageIdFromNewestPage(
  newestFirstPage: MessageDto[],
): string | null {
  let bestId: string | null = null;
  let bestValue: bigint | null = null;
  for (const message of newestFirstPage) {
    if (isOutboundMessage(message)) continue;
    const value = BigInt(message.messageId);
    if (bestValue == null || value > bestValue) {
      bestValue = value;
      bestId = message.messageId;
    }
  }
  return bestId;
}

export function patchConversationUnreadCount(
  items: ConversationListItemDto[],
  conversationId: number,
  unreadCount: number,
): ConversationListItemDto[] {
  return items.map((item) =>
    item.conversationId === conversationId ? { ...item, unreadCount } : item,
  );
}

export function formatMessageTime(iso: string | null, locale: string): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

export function formatListTime(iso: string | null, locale: string): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}
